import { useEffect, useRef, useState } from "react";
import type { DropdownOption } from "../types";

export const MatchesDropdown = <T extends string,>({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: string;
  value: T;
  options: Array<DropdownOption<T>>;
  onChange: (next: T) => void;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const currentLabel = options.find((option) => option.value === value)?.label ?? "";

  useEffect(() => {
    if (!open || disabled) return;
    const handleClick = (event: globalThis.MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, disabled]);

  return (
    <div
      className={`matches-dropdown ${open ? "is-open" : ""} ${
        disabled ? "is-disabled" : ""
      }`}
      ref={wrapperRef}
    >
      <button
        type="button"
        className="matches-dropdown-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
      >
        <span className="matches-dropdown-label">{label}</span>
        <span className="matches-dropdown-value">{currentLabel}</span>
        <span className="matches-dropdown-caret" aria-hidden="true" />
      </button>
      {open && (
        <div className="matches-dropdown-menu" role="listbox">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`matches-dropdown-item ${
                option.value === value ? "is-selected" : ""
              }`}
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
